import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { ENDPOINTS } from '../../utils/api-config';
import { FaHistory, FaSearch, FaComments, FaCircle } from 'react-icons/fa';
import './ChatHistory.css';
import ChatDetail from './ChatDetail';

const ChatHistory = () => {
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedChannel, setSelectedChannel] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');

  const fetchChannels = async (showLoading = false) => {
    try {
      if (showLoading) setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(ENDPOINTS.SENDBIRD.CHANNELS, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const rawData = response.data;
      const extractedChannels = 
        rawData.data?.channels || 
        rawData.data?.data?.channels || 
        rawData.data || 
        [];
        
      setChannels(extractedChannels);
      setError(null);
    } catch (err) {
      console.error('Error fetching chat history:', err);
      if (showLoading) setError('Failed to load chat history. Please ensure backend is running.');
    } finally {
      if (showLoading) setLoading(false);
    }
  };

  useEffect(() => {
    fetchChannels(true);
    const interval = setInterval(() => fetchChannels(false), 10000);
    return () => clearInterval(interval);
  }, []);

  const filteredChannels = channels.filter(channel => {
    const memberNames = channel.members.map(m => m.nickname?.toLowerCase() || '').join(' ');
    const matchesSearch = memberNames.includes(searchTerm.toLowerCase());
    
    if (filterType === 'all') return matchesSearch;
    
    const hasFamily = channel.members.some(m => m.role === 'customer');
    const hasProvider = channel.members.some(m => m.role === 'provider');
    
    if (filterType === 'family') return matchesSearch && hasFamily;
    if (filterType === 'provider') return matchesSearch && hasProvider;
    
    return matchesSearch;
  });

  if (loading && !selectedChannel) {
    return (
      <div className="no-selection" style={{ height: '100vh' }}>
        <div className="spinner"></div>
        <p>Loading your premium dashboard...</p>
      </div>
    );
  }

  return (
    <div className="chat-history-container">
      <div className="chat-history-sidebar">
        <div className="chat-history-header">
          <h2><FaHistory style={{ color: 'var(--primary-color)' }} /> Chat History</h2>
          
          <div className="filter-tabs">
            <button className={filterType === 'all' ? 'active' : ''} onClick={() => setFilterType('all')}>All</button>
            {/* <button className={filterType === 'family' ? 'active' : ''} onClick={() => setFilterType('family')}>Families</button>
            <button className={filterType === 'provider' ? 'active' : ''} onClick={() => setFilterType('provider')}>Providers</button> */}
          </div>

          <div className="search-box">
            <FaSearch />
            <input 
              type="text" 
              placeholder="Search conversations..." 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        <div className="channel-list">
          {filteredChannels.length === 0 ? (
            <div className="no-channels">
              <FaComments style={{ fontSize: '32px', opacity: 0.2, marginBottom: '8px' }} />
              <p>No conversations found.</p>
            </div>
          ) : (
            filteredChannels.map(channel => {
              let displayName = '';
              const customer = channel.members.find(m => m.role === 'customer');
              const provider = channel.members.find(m => m.role === 'provider');
              
              if (customer && provider) {
                displayName = `${customer.nickname} ↔ ${provider.nickname}`;
              } else {
                const names = new Set();
                channel.members?.forEach(m => { if (m.nickname) names.add(m.nickname); });
                if (channel.created_by?.nickname) {
                  names.add(channel.created_by.nickname);
                }
                if (channel.last_message?.user?.nickname) {
                  names.add(channel.last_message.user.nickname);
                }
                if (names.size < 2 && channel.name && channel.name !== 'Group Channel') {
                  channel.name.split(',').forEach(n => { if (n.trim()) names.add(n.trim()); });
                }
                displayName = Array.from(names).join(', ') || 'Unknown Chat';
              }

              return (
                <div 
                  key={channel.channel_url} 
                  className={`channel-item ${selectedChannel?.channel_url === channel.channel_url ? 'active' : ''}`}
                  onClick={() => setSelectedChannel(channel)}
                >
                  <div className="channel-info">
                    <div className="channel-name">
                      {channel.members && channel.members.length > 1 ? (
                        channel.members.map((member, idx) => (
                          <span key={member.user_id || idx} className="member-name-wrapper">
                            <img 
                              src={member.profile_url || 'https://static.sendbird.com/sample/cover/cover_15.jpg'} 
                              alt={member.nickname} 
                              className="avatar-inline" 
                              onError={(e) => { e.target.src = 'https://static.sendbird.com/sample/cover/cover_15.jpg'; }}
                            />
                            <span className="member-nickname">{member.nickname || 'Unknown'}</span>
                            {idx < channel.members.length - 1 && <span className="channel-separator">,&nbsp;</span>}
                          </span>
                        ))
                      ) : (
                        displayName
                      )}
                    </div>
                    <div className="channel-last-msg">
                      {channel.last_message?.message || 'No messages yet'}
                    </div>
                  </div>
                  <div className="channel-meta">
                    <div className="channel-date">
                      {channel.last_message ? new Date(channel.last_message.created_at).toLocaleDateString([], { month: 'short', day: 'numeric' }) : ''}
                    </div>
                    {channel.unread_message_count > 0 && (
                      <div className="unread-badge">{channel.unread_message_count}</div>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      <div className="chat-history-main">
        {selectedChannel ? (
          <ChatDetail channel={selectedChannel} />
        ) : (
          <div className="no-selection">
            <FaComments style={{ fontSize: '64px', marginBottom: '24px', opacity: 0.1, color: 'var(--primary-color)' }} />
            <h3>Select a conversation</h3>
            <p>Choose a chat from the sidebar to view the full history in premium mode.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ChatHistory;
